import {
  AloftDateJSON,
  CompType,
  FeatureAreaKey,
  ListingType,
  PropertyData,
  schSourceType,
} from '@aloft/toolkit-shared';

import { useAutofillProperty } from '../../../hooks/useAutofillProperty.ts';
import { CompSelectionState } from '../types.ts';
import { applyDefaultFeatureAreas } from './applyDefaultFeatureAreas.ts';
import { listingToPropertyData } from './listingToPropertyData.ts';

const addUADRatingsIfMissing = (
  property: PropertyData,
  selection: CompSelectionState[string] | undefined,
  uadToCraftsmanQuality?: Record<string, number>,
): PropertyData => {
  const resolvedUadQuality =
    property.editableData.improvements.uadQuality ?? selection?.uadQualityRating;
  const existingCraftsmanQuality = property.editableData.improvements.craftsmanQuality;

  let craftsmanQuality = existingCraftsmanQuality;
  let craftsmanQualityFromSettings = false;

  if (!existingCraftsmanQuality && resolvedUadQuality && uadToCraftsmanQuality) {
    const mapped = uadToCraftsmanQuality[String(resolvedUadQuality)];
    if (mapped !== undefined) {
      craftsmanQuality = mapped;
      craftsmanQualityFromSettings = true;
    } else {
      craftsmanQuality = resolvedUadQuality;
    }
  } else if (!existingCraftsmanQuality) {
    craftsmanQuality = resolvedUadQuality;
  }

  return {
    ...property,
    editableData: {
      ...property.editableData,
      improvements: {
        ...property.editableData.improvements,
        aiUadQuality: property.editableData.improvements.aiUadQuality ?? selection?.uadQualityRating,
        aiUadCondition:
          property.editableData.improvements.aiUadCondition ?? selection?.uadConditionRating,
        uadQuality: resolvedUadQuality,
        uadCondition:
          property.editableData.improvements.uadCondition ?? selection?.uadConditionRating,
        craftsmanQuality,
      },
    },
    selectedSources: craftsmanQualityFromSettings
      ? {
          ...property.selectedSources,
          editableData: {
            ...property.selectedSources?.editableData,
            improvements: {
              ...property.selectedSources?.editableData?.improvements,
              craftsmanQuality: { source: schSourceType.enum.estimated },
            },
          },
        }
      : property.selectedSources,
  };
};

export const handleAddClickForCompCreation = async ({
  isLoading,
  rowSelection,
  createComps,
  autofillProperty,
  effectiveDate,
  orderID,
  isInspectifyOrder,
  hasArvOption,
  listingType,
  defaultFeatureAreas,
  uadToCraftsmanQuality,
}: {
  isLoading: boolean;
  rowSelection: CompSelectionState;
  createComps: (comps: PropertyData[]) => void;
  autofillProperty: ReturnType<typeof useAutofillProperty>;
  effectiveDate: AloftDateJSON;
  orderID: string;
  isInspectifyOrder: boolean;
  hasArvOption: boolean;
  listingType?: ListingType;
  defaultFeatureAreas?: Partial<Record<FeatureAreaKey, number>>;
  uadToCraftsmanQuality?: Record<string, number>;
}) => {
  const listingToProp = listingToPropertyData(
    autofillProperty,
    effectiveDate,
    orderID,
    listingType,
  );

  // Note: Our query only returns properties of the same type as the subject, so using the subject's property type for all comps
  if (!isLoading) {
    if (isInspectifyOrder && hasArvOption) {
      // New behavior: create separate comps for each selected comp type (AIV/ARV)
      const properties: Promise<PropertyData>[] = Object.values(rowSelection)
        .filter((selection) => selection.listingData && (selection.aiv || selection.arv))
        .flatMap((selection) => {
          const compTypes: CompType[] = [];
          if (selection.aiv) compTypes.push('aiv');
          if (selection.arv) compTypes.push('arv');

          // Create a separate PropertyData for each comp type selected
          return compTypes.map(async (compType) => {
            let property = addUADRatingsIfMissing(
              await listingToProp(selection.listingData!),
              selection,
              uadToCraftsmanQuality,
            );
            if (defaultFeatureAreas) property = applyDefaultFeatureAreas(property, defaultFeatureAreas);
            return { ...property, compType };
          });
        });

      createComps(await Promise.all(properties));
    } else {
      // Old behavior: create comps without compType for non-Inspectify orders or Inspectify orders without ARV option
      const properties: Promise<PropertyData>[] = Object.values(rowSelection)
        .filter(
          (c) => c.listingData && (c.listingData?.ToolkitListingID || c.listingData?.ListingId),
        )
        .map(async (selection) => {
          let property = addUADRatingsIfMissing(
            await listingToProp(selection.listingData!),
            selection,
            uadToCraftsmanQuality,
          );
          if (defaultFeatureAreas) property = applyDefaultFeatureAreas(property, defaultFeatureAreas);
          return property;
        });

      createComps(await Promise.all(properties));
    }
  }
};
